import "./Calendar.css";

import classNames from "classnames";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Calendar, DayPropGetter, momentLocalizer } from "react-big-calendar";
import { CgWorkAlt } from "react-icons/cg";
import { FaCircle } from "react-icons/fa";
import { IoMdArrowBack } from "react-icons/io";
import { IoArrowForward } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { Tooltip as ReactTooltip } from "react-tooltip";

import { AvatarPlaceholder } from "../../components/avatar/AvatarPlaceholder";
import Button from "../../components/button/Button";
import { LottieLoading } from "../../components/graphics/LottieLoading";
import { PageLayout } from "../../components/layouts/PageLayout";
import CustomSelect from "../../components/select/Select";
import CalendarSvg from "../../components/svg/CalendarSvg";
import { getInitialsFromFullName } from "../../constants/constants";
import { WorkModel } from "../../factories/works/models/work.model";
import { useAppSelector } from "../../redux/redux";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useGetUsersQuery } from "../../slices/UserSlice";
import { useGetWorksQuery } from "../../slices/WorkSlice";
import { useGetWorkTypesQuery } from "../../slices/WorkTypeSlice";
import { FormSection } from "../../support/FormSection";
import { SelectOptionType } from "../../types/types";
import { compareSelectOptionsAlphabeticallyWithNumbersFirst } from "../../utils/sort";
import { WorkTableSort } from "../works/types/work-table";
import { useResizeKey } from "./useResizeKey";

enum ActivePeriod {
  Day = "day",
  Week = "week",
  Month = "month",
}

/** Space above/below the month grid (top bar, title, filters, page padding). */
const MONTH_VIEW_HEIGHT = "max(480px, calc(100vh - 300px))";
/** Smallest month grid when the window is short (QA batch 15, CAL-011). */
const MONTH_VIEW_MIN_HEIGHT = 320;

const CUSTOMER_TOOLTIP_ID = "calendar-customer-name-tooltip";
const WORK_TOOLTIP_ID = "calendar-work-name-tooltip";

type CalendarWorkEvent = Omit<WorkModel, "dueDate"> & {
  color?: string;
  dueDate: Date;
};

const getCalendarDateRange = (date: Date, activePeriod: ActivePeriod) => {
  if (activePeriod === ActivePeriod.Day) {
    return {
      fromDate: moment(date).startOf("day").toDate(),
      toDate: moment(date).endOf("day").toDate(),
    };
  }

  if (activePeriod === ActivePeriod.Week) {
    return {
      fromDate: moment(date).startOf("week").toDate(),
      toDate: moment(date).endOf("week").toDate(),
    };
  }

  return {
    fromDate: moment(date).startOf("month").toDate(),
    toDate: moment(date).endOf("month").toDate(),
  };
};

const getCalendarTitle = (date: Date, activePeriod: ActivePeriod) => {
  if (activePeriod === ActivePeriod.Day) {
    return moment(date).format("MMMM DD, YYYY");
  }

  if (activePeriod === ActivePeriod.Week) {
    const start = moment(date).startOf("week");
    const end = moment(date).endOf("week");

    return `${start.format("MMM DD")} - ${end.format("MMM DD, YYYY")}`;
  }

  return moment(date).format("MMMM YYYY");
};

export const CalendarView = () => {
  const orgId = getLinkedFirm()?.orgId || "";

  const navigate = useNavigate();
  const user = useAppSelector((state) => state.appReducer.user);
  // Month view re-measures "+N more" only when mounted (see useResizeKey).
  const resizeKey = useResizeKey();
  // QA batch 15 (CAL-011): on short windows (1366x768) the fixed formula left
  // the last week below the fold. After the month grid is drawn, it is made
  // smaller by however much the page still scrolls (never below 320px).
  const monthWrapRef = useRef<HTMLDivElement | null>(null);
  const [monthHeight, setMonthHeight] = useState<number | null>(null);

  const [selectedWorkTypes, setSelectedWorkTypes] = useState<
    SelectOptionType[]
  >([]);
  const [selectedUsers, setSelectedUsers] = useState<SelectOptionType[]>([]);
  const [activePeriod, setActivePeriod] = useState<ActivePeriod>(
    ActivePeriod.Month,
  );
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  const { data: users, isFetching: isLoadingUsers } = useGetUsersQuery(orgId, {
    skip: !orgId,
  });

  const { data: workTypes, isFetching: isLoadingWorkTypes } =
    useGetWorkTypesQuery(
      { orgId },
      {
        skip: !orgId,
      },
    );

  const worksQueryParams = useMemo(() => {
    const { fromDate, toDate } = getCalendarDateRange(
      currentDate,
      activePeriod,
    );

    return {
      pageNumber: 1,
      pageSize: 100,

      customerIds: [],
      workName: [],
      userIds: selectedUsers.map((item) => item.value),
      workStatuses: [],
      worktypeIds: selectedWorkTypes.map((item) => item.value),

      startDates: [],
      dueDates: ["BetweenDates"],
      fromDate,
      toDate,

      orgId,
      sortBy: WorkTableSort.WorkName,
      sortOrder: "asc" as const,
      currentUserId: user?.id,
      isArchived: false,
      hideGreenStatus: false,
    };
  }, [
    activePeriod,
    currentDate,
    orgId,
    selectedUsers,
    selectedWorkTypes,
    user?.id,
  ]);

  const { data: works = [], isFetching: isLoadingWorks } = useGetWorksQuery(
    worksQueryParams,
    {
      skip: !orgId,
    },
  );

  useEffect(() => {
    setMonthHeight(null);
    if (activePeriod !== ActivePeriod.Month || isLoadingWorks) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        const wrap = monthWrapRef.current;
        const scroller =
          (wrap?.closest("#proc-main-view") as HTMLElement | null) ??
          (document.scrollingElement as HTMLElement | null);
        if (!wrap || !scroller) return;
        const overflow = scroller.scrollHeight - scroller.clientHeight;
        if (overflow <= 0) return;
        const current = wrap.getBoundingClientRect().height;
        setMonthHeight(
          Math.max(MONTH_VIEW_MIN_HEIGHT, Math.floor(current - overflow)),
        );
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [activePeriod, isLoadingWorks, resizeKey]);

  const calendarTitle = useMemo(
    () => getCalendarTitle(currentDate, activePeriod),
    [activePeriod, currentDate],
  );

  const calendarEvents = useMemo<CalendarWorkEvent[]>(
    () =>
      works.map((item) => ({
        ...item,
        dueDate: new Date(item.dueDate),
      })),
    [works],
  );

  const dayPropGetter: DayPropGetter = useCallback(() => {
    if (activePeriod === ActivePeriod.Day) {
      return {
        style: {
          backgroundColor: "white",
        },
      };
    }

    if (activePeriod === ActivePeriod.Week) {
      // No min height here: react-big-calendar also applies these styles to
      // the date header and the all-day row, which made a big empty band at
      // the top of the week view (Roadmap 1793).
      return {
        style: {
          backgroundColor: "white",
        },
      };
    }

    return {};
  }, [activePeriod]);

  const handlePreviousDate = useCallback(() => {
    if (activePeriod === ActivePeriod.Month) {
      setCurrentDate((prevDate) =>
        moment(prevDate).subtract(1, "month").toDate(),
      );
      return;
    }

    if (activePeriod === ActivePeriod.Week) {
      setCurrentDate((prevDate) =>
        moment(prevDate).subtract(1, "week").toDate(),
      );
      return;
    }

    setCurrentDate((prevDate) => moment(prevDate).subtract(1, "day").toDate());
  }, [activePeriod]);

  const handleNextDate = useCallback(() => {
    if (activePeriod === ActivePeriod.Month) {
      setCurrentDate((prevDate) => moment(prevDate).add(1, "month").toDate());
      return;
    }

    if (activePeriod === ActivePeriod.Week) {
      setCurrentDate((prevDate) => moment(prevDate).add(1, "week").toDate());
      return;
    }

    setCurrentDate((prevDate) => moment(prevDate).add(1, "day").toDate());
  }, [activePeriod]);

  const usersOptions = useMemo<SelectOptionType[]>(() => {
    return (
      users?.map((item) => ({
        avatar: getInitialsFromFullName(
          `${item?.userProfile?.firstName} ${item?.userProfile?.lastName}`,
        ),
        avatarCustomColor: item?.userProfile?.color,
        label: `${item?.userProfile?.firstName} ${item?.userProfile?.lastName}`,
        value: `${item?.id}`,
      })) || []
    );
  }, [users]);

  const workTypeOptions = useMemo<SelectOptionType[]>(() => {
    return (
      workTypes
        ?.map((item) => ({
          label: item?.name ?? "",
          value: item?.id,
        }))
        .sort(compareSelectOptionsAlphabeticallyWithNumbersFirst) || []
    );
  }, [workTypes]);

  const handleClearFilters = useCallback(() => {
    setSelectedUsers([]);
    setSelectedWorkTypes([]);
  }, []);

  return (
    <div className={"flex justify-center"}>
      <div
        className={
          "w-fit pl-24 pr-32 xl:min-w-full xl:pl-16 xl:pr-24 2xl:min-w-[1300px] 3xl:min-w-[1500px] 4xl:min-w-[1700px]"
        }>
        <PageLayout
          title={
            <div className={"mb-6 flex items-center gap-4"}>
              <div
                className={
                  "bg-purple flex h-12 w-12 items-center justify-center rounded-full text-white"
                }>
                <CalendarSvg />
              </div>

              <div className={"w-fit font-medium"}>{calendarTitle}</div>

              <div className={"flex w-[100px] items-center justify-between"}>
                <Button
                  colorType={"ghost"}
                  icon={<IoMdArrowBack size={"24px"} />}
                  label={""}
                  size={"small"}
                  extraClasses={"flex-nowrap py-0 px-0"}
                  onClick={handlePreviousDate}
                />

                <div>
                  <FaCircle size={"6px"} />
                </div>

                <Button
                  colorType={"ghost"}
                  icon={<IoArrowForward size={"24px"} />}
                  label={""}
                  size={"small"}
                  extraClasses={"flex-nowrap py-0 px-0"}
                  onClick={handleNextDate}
                />
              </div>
            </div>
          }>
          <div
            className={
              "relative z-[2] flex h-fit w-full items-end justify-between"
            }>
            <div className={"flex items-end gap-4"}>
              <div className={"min-w-[200px]"}>
                <div className={"mb-0.5 text-base"}>Assignee</div>

                <CustomSelect
                  options={usersOptions}
                  value={selectedUsers}
                  onChange={(newValue) => {
                    setSelectedUsers(newValue as SelectOptionType[]);
                  }}
                  isMulti
                  withMenu
                  isLoading={isLoadingUsers}
                  placeholder={"All"}
                  selectStyle={{ fitChips: true }}
                />
              </div>

              <div className={"min-w-[200px]"}>
                <div className={"mb-0.5 text-base"}>Work type</div>

                <CustomSelect
                  options={workTypeOptions}
                  value={selectedWorkTypes}
                  onChange={(newValue) => {
                    setSelectedWorkTypes(newValue as SelectOptionType[]);
                  }}
                  isMulti
                  withMenu
                  isLoading={isLoadingWorkTypes}
                  placeholder={"All"}
                  selectStyle={{ fitChips: true }}
                />
              </div>

              {Boolean(
                selectedUsers.length > 0 || selectedWorkTypes.length > 0,
              ) && (
                <Button
                  label={"Clear all"}
                  onClick={handleClearFilters}
                  extraClasses={
                    "normal-case py-3 px-2 max-h-[42px] min-h-fit h-fit"
                  }
                  colorType={"ghost"}
                />
              )}
            </div>

            <div
              className={
                "flex h-fit items-center gap-4 rounded-[8px] border-[1px] border-gray-300 bg-white p-2"
              }>
              <Button
                label={"Day"}
                extraClasses={"normal-case w-[100px] min-h-fit h-fit py-3"}
                size={"small"}
                colorType={
                  activePeriod === ActivePeriod.Day ? "secondary" : "ghost"
                }
                onClick={() => setActivePeriod(ActivePeriod.Day)}
              />

              <Button
                label={"Week"}
                extraClasses={"normal-case w-[100px] min-h-fit h-fit py-3"}
                size={"small"}
                colorType={
                  activePeriod === ActivePeriod.Week ? "secondary" : "ghost"
                }
                onClick={() => setActivePeriod(ActivePeriod.Week)}
              />

              <Button
                label={"Month"}
                extraClasses={"normal-case w-[100px] min-h-fit h-fit py-3"}
                size={"small"}
                colorType={
                  activePeriod === ActivePeriod.Month ? "secondary" : "ghost"
                }
                onClick={() => setActivePeriod(ActivePeriod.Month)}
              />
            </div>
          </div>

          <FormSection
            name={""}
            showHeading={false}
            extraCx={"px-0 py-0 overflow-visible"}>
            {isLoadingWorks ? (
              <LottieLoading />
            ) : (
              <div
                ref={monthWrapRef}
                className={classNames(
                  "relative",
                  activePeriod === ActivePeriod.Week && "calendar-week-view",
                )}>
                {activePeriod === ActivePeriod.Day && (
                  <div
                    className={"absolute left-0 top-0 z-10 h-[72px] w-[95px]"}>
                    <div
                      className={
                        "h-full border border-gray-200 bg-white pb-8 text-center text-sm font-bold uppercase text-gray-700"
                      }>
                      {moment(currentDate).format("ddd D")}
                    </div>
                  </div>
                )}

                <Calendar
                  key={
                    activePeriod === ActivePeriod.Month
                      ? `month-${resizeKey}-${monthHeight ?? "auto"}`
                      : activePeriod
                  }
                  localizer={momentLocalizer(moment)}
                  toolbar={false}
                  view={activePeriod}
                  style={{
                    // Month view fits the window without scrolling (Roadmap 1792).
                    height:
                      activePeriod === ActivePeriod.Month
                        ? monthHeight
                          ? `${monthHeight}px`
                          : MONTH_VIEW_HEIGHT
                        : "fit-content",
                  }}
                  step={activePeriod === ActivePeriod.Week ? 60 : 30}
                  timeslots={1}
                  startAccessor={(event) => {
                    const date = new Date(event.dueDate);

                    return new Date(date.setHours(7, 0, 0, 0));
                  }}
                  endAccessor={(event) => {
                    const date = new Date(event.dueDate);

                    return new Date(date.setHours(23, 59, 0, 0));
                  }}
                  titleAccessor={"workName"}
                  events={calendarEvents}
                  popup={true}
                  dayPropGetter={dayPropGetter}
                  date={currentDate}
                  onSelectEvent={(event) => {
                    navigate(`/work/${event.workId}`);
                  }}
                  eventPropGetter={(event) => {
                    return {
                      style: {
                        backgroundColor: event.color,
                        color: "white",
                        borderRadius: "8px",
                        // Month view: padding and height come from Calendar.css so
                        // the row-limit measurement matches the real items.
                        padding:
                          activePeriod === ActivePeriod.Month
                            ? undefined
                            : "4px",
                        border: "none",
                        minHeight:
                          activePeriod === ActivePeriod.Month
                            ? undefined
                            : "80px",
                        maxHeight:
                          activePeriod === ActivePeriod.Month
                            ? undefined
                            : "80px",
                      },
                    };
                  }}
                  components={{
                    event: ({ event }) =>
                      activePeriod === ActivePeriod.Month ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}>
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                            data-tooltip-id={CUSTOMER_TOOLTIP_ID}
                            data-tooltip-content={event.customerName}>
                            <AvatarPlaceholder
                              type={"custom"}
                              size={"2xs"}
                              label={getInitialsFromFullName(
                                event.customerName,
                              )}
                            />

                            <span
                              style={{
                                marginLeft: "8px",
                                width: "80px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}>
                              {event.customerName}
                            </span>
                          </div>

                          <div
                            data-tooltip-id={WORK_TOOLTIP_ID}
                            data-tooltip-content={event.workName}>
                            <CgWorkAlt color={"white"} className={"h-6 w-6"} />
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "start",
                            justifyContent: "space-between",
                            height: "100%",
                          }}>
                          <div
                            style={{
                              marginInline: "8px",
                              width: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              marginTop: "4px",
                              height: "fit",
                              paddingBottom: "4px",
                            }}>
                            {event.workName}
                          </div>

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "8px",
                              paddingLeft: "4px",
                            }}>
                            <AvatarPlaceholder
                              type={"custom"}
                              size={"2xs"}
                              label={getInitialsFromFullName(
                                event.customerName,
                              )}
                            />

                            <span
                              style={{
                                marginLeft: "8px",
                                width: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                paddingBottom: "4px",
                              }}>
                              {event.customerName}
                            </span>
                          </div>
                        </div>
                      ),
                  }}
                />
              </div>
            )}
          </FormSection>
        </PageLayout>
      </div>

      <ReactTooltip
        id={CUSTOMER_TOOLTIP_ID}
        place={"top"}
        positionStrategy={"fixed"}
        className={"z-10 !rounded-[8px] !p-2 !text-sm"}
      />

      <ReactTooltip
        id={WORK_TOOLTIP_ID}
        place={"top"}
        positionStrategy={"fixed"}
        className={"z-10 !rounded-[8px] !p-2 !text-sm"}
      />
    </div>
  );
};
